import { useApiClient } from "../../../../providers/api-client";
import { useStyles } from "../../../../providers/styles/use-styles";
import Button from "../../../components/Button/Button";
import { Game, Question, QuestionType } from "../../../models/game.entity";
import styles from "./questions.module.css";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import ProgressBar, { ProgressBarHandle } from "../ProgressBar/ProgressBar";

interface Props {
  token: string;
  game: Game;
  questionsNumber: number;
  secondsToAnswer?: number;
  onGameFinished: () => void;
}

const Questions = ({ token, game, questionsNumber, secondsToAnswer, onGameFinished }: Props) => {
  const client = useApiClient();
  const { getStyles } = useStyles();

  const progressBarRef = useRef<ProgressBarHandle>(null);
  const [isLoading, setLoading] = useState(false);
  const [questions, setQuestions] = useState<Question[]>();
  const [position, setPosition] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string>();
  const [openAnswer, setOpenAnswer] = useState<string>();
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    setQuestions(game.questions);
    setPosition(game.position);
    handleRestart();
  }, []);

  async function submitResult() {
    handlePause();
    setLoading(true);

    try {
      await client.postQuestionAnswer(
        token,
        questions!![position].id,
        selectedAnswer,
        openAnswer
      );

      if (position < questionsNumber - 1) {
        setPosition(position + 1);
        setSelectedAnswer(undefined);
        setOpenAnswer("");
        handleRestart();
      } else {
        onGameFinished();
      }
    } catch (error: any) {
      console.log(error);
      toast.error("Не удалось выполнить запрос. Попробуйте еще раз.", {
        position: "top-center",
        hideProgressBar: true,
        icon: false,
        closeButton: false,
      });
    } finally {
      setLoading(false);
    }
  }

  const handleFired = () => {
    console.log('Progress completed and fired!');
    submitResult();
  };

  const handlePause = () => {
    if (progressBarRef.current) {
      console.log("On timer pause")
      progressBarRef.current.pause();
      setIsPaused(true);
    }
  };

  const handleRestart = () => {
    console.log("On timer restart")
    if (progressBarRef.current) {
      progressBarRef.current.restart();
      setIsPaused(false);
    }
  };

  return (
    <div className={styles.main}>
      {questions && (
        <>
          {secondsToAnswer && (
            <div
              className={styles.progressBarContainer}
            >
              <ProgressBar
                ref={progressBarRef}
                isPaused={isPaused}
                secondsToFire={secondsToAnswer}
                onFired={handleFired}
              />
            </div>
          )}
          <h3
            className={styles.title}
            style={getStyles(["txt_label_primary_base"])}
          >
            {" "}
            {position + 1} / {questionsNumber}
          </h3>

          <div
            className={styles.question}
            style={getStyles(["txt_card_primary_base", "bg_card_primary_base"])}
          >
            {questions[position].text}
          </div>

          <div className={styles.answersWrapper}>
            {questions[position].type === QuestionType.singleChoice && (
              <>
                {questions[position].answers.map((answer, index) => (
                  <div
                    className={styles.answer}
                    key={index}
                    onClick={() => !isLoading && setSelectedAnswer(answer.id)}
                    style={{
                      backgroundColor:
                        answer.id === selectedAnswer ? "white" : "transparent",
                      color: answer.id === selectedAnswer ? "#2059FF" : "white",
                      opacity: isLoading ? 0.7 : 1,
                      ...getStyles(
                        answer.id === selectedAnswer
                          ? [
                            "txt_chips_primary_selected",
                            "bg_chips_primary_selected",
                          ]
                          : [
                            "txt_chips_primary_base",
                            "border_chips_primary_base",
                          ]
                      ),
                    }}
                  >
                    {answer.text}
                  </div>
                ))}
              </>
            )}

            {questions[position].type === QuestionType.open && (
              <input
                className={styles.input}
                style={getStyles([
                  "txt_chips_primary_base",
                  "border_chips_primary_base",
                ])}
                placeholder="Введите ваш ответ"
                value={openAnswer}
                disabled={isLoading || isPaused}
                onChange={(e) => setOpenAnswer(e.target.value)}
                maxLength={40}
              />
            )}
          </div>

          <div className={styles.spacer} />

          <Button
            title={
              isLoading
                ? "Загрузка"
                : position < questionsNumber - 1
                  ? "Следующий вопрос"
                  : "Завершить"
            }
            onClick={submitResult}
            isLoading={isLoading}
          />
        </>
      )}
    </div>
  );
};

export default Questions;
